/**
 * vm-quick-add.js (v6.7, 30 Oct 2026): quick add on the "Goes well with" cards in the PDP buy box.
 * Markup: blocks/vm-goes-well-with.liquid, one <vm-quick-add> per card.
 *
 * 1. One variant (the beanie): the card's Add button posts it to /cart/add.js in one tap.
 * 2. More variants: Add opens a native modal <dialog> (focus stays inside, Esc and a backdrop click close
 *    it, focus returns to Add) with the product page's chips. As on the product page (patch 12): Size is
 *    never preselected; with no size, Add to cart says "Choose a size." (role=alert) and puts focus on the
 *    first size; a size the chosen color or style lacks reads "S, not available", a sold-out one
 *    "M, sold out". A color or style the other choices lack moves them to the first variant that has it.
 *    The price follows the choices ("From $69" until they settle on one price).
 * 3. Both post ONE /cart/add.js with the rendered cart sections and dispatch Horizon's own
 *    CartLinesUpdateEvent (@shopify/events, as vm-addon.js does), so the cart drawer, its lines and the
 *    cart bubble refresh with no extra fetch, and the drawer opens (cart-drawer.js waits for this dialog
 *    to close first).
 * 4. A failed add says "We couldn't update your cart. Try again." and keeps the choices.
 * Never touches the page's own form or picker: vm-pdp.js reads fieldset[data-vm-opt] and guards only
 * product-form-component; these fieldsets carry data-vm-qa-opt and sit outside the product form. No form
 * here posts to /cart/add (Shopify's shop_events_listener would take it for the page's product form); the
 * add is still counted, from the /cart/add.js response, as for the main button.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const ALERT =
  '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="6.5"/><path d="M8 4.5v4.2M8 10.8v.7"/></svg>';
const FAIL = "We couldn't update your cart. Try again.";

class VmQuickAdd extends HTMLElement {
  #ac = null;
  variants = [];

  connectedCallback() {
    this.cfg = this.closest('.vm-gww')?.dataset || {};
    this.dialog = this.querySelector('dialog');
    this.#ac = new AbortController();
    const o = { signal: this.#ac.signal };
    this.querySelector('[data-vm-qa-now]')?.addEventListener('click', this.#now, o);
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
    this.dialog.querySelector('form')?.addEventListener('submit', this.#submit, o);
    try {
      this.variants = JSON.parse(this.dialog.querySelector('[data-vm-qa-variants]')?.textContent || '[]');
    } catch {
      this.variants = [];
    }
    this.trigger = this.querySelector('[data-vm-qa-open]');
    this.trigger?.addEventListener('click', () => this.open(), o);
    this.dialog.addEventListener('click', this.#onClick, o);
    this.dialog.addEventListener('change', this.#onChange, o);
    this.dialog.addEventListener('close', () => this.trigger?.focus({ preventScroll: true }), o);
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    if (this.dialog?.open) this.dialog.close();
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.#fail(false);
    this.dialog.showModal();
  }

  #onClick = (e) => {
    if (e.target === this.dialog || e.target.closest?.('[data-vm-qa-close]')) this.dialog.close();
  };

  get #sets() {
    return [...this.dialog.querySelectorAll('fieldset[data-vm-qa-opt]')];
  }

  /** The choice at each option position: a drawn option's checked chip, a one-value option's only value. */
  #sel() {
    const sel = [...(this.variants[0]?.[1] || [])];
    for (const fs of this.#sets) sel[Number(fs.dataset.vmQaOpt)] = fs.querySelector('input:checked')?.value ?? null;
    return sel;
  }

  #match(sel) {
    return this.variants.filter((v) => sel.every((x, i) => x == null || v[1][i] === x));
  }

  #onChange = (e) => {
    const t = e.target;
    const fs = t instanceof HTMLInputElement && t.type === 'radio' ? t.closest('fieldset[data-vm-qa-opt]') : null;
    if (!fs) return;
    if (fs.dataset.kind !== 'size') this.#fit(Number(fs.dataset.vmQaOpt));
    this.#error(false);
    this.#fail(false);
    this.#sync();
  };

  /** A color or style the other choices lack: move those choices to the first variant that has it. */
  #fit(i) {
    const sel = this.#sel();
    const sizes = this.#sets.filter((fs) => fs.dataset.kind === 'size').map((fs) => Number(fs.dataset.vmQaOpt));
    if (!this.#match(sel.map((x, j) => (sizes.includes(j) ? null : x))).length) {
      const v = this.variants.find((x) => x[1][i] === sel[i] && x[2]) || this.variants.find((x) => x[1][i] === sel[i]);
      for (const fs of this.#sets) {
        const j = Number(fs.dataset.vmQaOpt);
        if (!v || j === i || fs.dataset.kind === 'size') continue;
        const r = [...fs.querySelectorAll('input')].find((x) => x.value === v[1][j]);
        if (r) r.checked = true;
      }
    }
  }

  /** Chip states, the chosen value next to each label, and the price. */
  #sync() {
    for (const fs of this.#sets) {
      const i = Number(fs.dataset.vmQaOpt);
      const size = fs.dataset.kind === 'size';
      for (const r of fs.querySelectorAll('input')) {
        const probe = this.#sel();
        probe[i] = r.value;
        // A size is checked against every other choice; a color or style stays selectable (see #fit).
        const pool = size ? this.#match(probe) : this.variants.filter((v) => v[1][i] === r.value);
        const state = !pool.length ? 'not available' : pool.some((v) => v[2]) ? '' : 'sold out';
        if (state && r.checked) r.checked = false;
        r.disabled = Boolean(state);
        r.setAttribute('aria-label', state ? `${r.value}, ${state}` : r.value);
        r.closest('.vm-chip')?.classList.toggle('vm-chip--sold', Boolean(state));
      }
      const shown = fs.querySelector('[data-vm-qa-sel]');
      if (shown) shown.textContent = fs.querySelector('input:checked')?.value || '';
    }
    const prices = [...new Set(this.#match(this.#sel()).filter((v) => v[2]).map((v) => v[3]))];
    const out = this.dialog.querySelector('[data-vm-qa-price]');
    if (out && prices.length) out.textContent = `${prices.length > 1 ? 'From ' : ''}${this.#money(Math.min(...prices))}`;
  }

  #money(cents) {
    const text = formatMoney(Math.round(cents), this.cfg.moneyFormat || '${{amount}}', this.cfg.currency || 'USD');
    return text.replace(/([.,])00(?!\d)/, '');
  }

  #error(on) {
    for (const fs of this.#sets) {
      const msg = fs.querySelector('[data-vm-qa-size-err]');
      if (!msg) continue;
      fs.classList.toggle('is-error', on);
      msg.innerHTML = on ? `${ALERT}<span>Choose a size.</span>` : '';
    }
  }

  /** The failure line inside `scope` (the dialog's form, or this card for the one-tap Add). */
  #fail(on, scope) {
    for (const el of this.querySelectorAll('[data-vm-qa-fail]')) el.textContent = on && scope?.contains(el) ? FAIL : '';
  }

  #now = (e) => this.#go(e.currentTarget, Number(e.currentTarget.dataset.vmQaNow) || 0, this);

  #submit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const missing = this.#sets.find((fs) => !fs.querySelector('input:checked'));
    if (missing) {
      if (missing.dataset.kind === 'size') this.#error(true);
      [...missing.querySelectorAll('input')].find((r) => !r.disabled)?.focus();
      return;
    }
    this.#go(form.querySelector('button[type="submit"]'), this.#match(this.#sel()).find((v) => v[2])?.[0] || 0, form);
  };

  async #go(button, id, scope) {
    if (!button || button.ariaBusy === 'true') return;
    if (!id) return this.#fail(true, scope);
    button.ariaBusy = 'true';
    this.#fail(false);
    const ok = await this.#add(id, button, scope);
    button.ariaBusy = null;
    if (ok && this.dialog?.open) this.dialog.close();
  }

  async #add(id, from, scope) {
    const done = CartLinesUpdateEvent.createPromise();
    let settled = false;
    from.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: done.promise,
      })
    );
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId))];
    try {
      const response = await fetch(this.cfg.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sections.join(','), sections_url: location.pathname }),
      });
      const data = await response.json().catch(() => ({}));
      const failed = !response.ok || Boolean(data.status);
      const cart = await fetch(this.cfg.cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      done.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: failed ? 0 : 1,
          sections: failed ? undefined : data.sections,
          source: 'vm-quick-add',
          sourceId: this.id,
          didError: failed,
        },
      });
      settled = true;
      if (failed) throw new Error(data.description || data.message || 'Not added');
      return true;
    } catch (error) {
      if (!settled) done.reject(error);
      console.warn('[vm-quick-add]', error);
      this.#fail(true, scope);
      return false;
    }
  }
}

if (!customElements.get('vm-quick-add')) customElements.define('vm-quick-add', VmQuickAdd);
